import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useSendMessage } from './useSendMessage';

export const useMessageInput = <
  T extends HTMLInputElement | HTMLTextAreaElement,
>() => {
  const { sendMessage } = useSendMessage();
  const inputRef = useRef<T | null>(null);

  // this should only be used for status checking and validation
  // source of truth for accessing the message content should be with inptuRef.current.value.
  const [message, setMessage] = useState<string>('');

  const handleSendMessage = useCallback(() => {
    if (inputRef.current && inputRef.current.value) {
      // Create a local messageId to track the message in case of failure
      // This id will be overridden by the server on a successful response
      const messageId = uuidv4();
      sendMessage(inputRef.current.value, messageId);
      inputRef.current.value = '';
      setMessage('');
    }
  }, [sendMessage]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const inputRefCB = useCallback(
    (node: T | null) => {
      inputRef.current = node;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (node) {
        abortControllerRef.current = new AbortController();
        node.addEventListener(
          'keydown',
          (e) => {
            e.stopPropagation();
            if ('key' in e && e.key === 'Enter') {
              e.preventDefault();
              handleSendMessage();
            }
          },
          {
            signal: abortControllerRef.current.signal,
          },
        );
        node.addEventListener(
          'input',
          () => {
            setMessage(node.value);
          },
          {
            signal: abortControllerRef.current.signal,
          },
        );
      }
    },
    [handleSendMessage],
  );

  return {
    inputRefCB,
    message,
    sendMessage: handleSendMessage,
  };
};
